/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useState, Fragment, useEffect } from 'react'
import {
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Box,
  Divider,
} from '@maas-components/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { Form, Formik, FormikProps } from 'formik'
import * as Analytics from 'core/common/analytics'
import { DEFAULT_VARIATION_OPTIONS_BY_TYPE } from 'core/common/constants/multivariation'
import i18n from 'core/common/i18n'
import { isBoolean } from 'core/common/utils/is'
import CardStep from 'core/components/CardStep'
import { Button } from 'core/components/Core'
import BulkModeButton from 'core/components/Multivariation/VariationValue/BulkModeButton'
import VariationItemView from 'core/components/Variations/VariationItemView'
import { useFormContext } from 'core/pages/ProductNew-v2/context/ProductNewContext'
import { TVariation, TProductInitialValues } from 'core/pages/ProductNew-v2/types'
import StockChannels from './components/StockChannels'
import { TInitialValues } from './constants'
import { SimpleProductStockFormSchema, VariationProductStockFormSchema } from './schema'
import { TProductStockProps, TStockInitialValues } from './types'

const allowedFields = ['quantity']

const ProductStockForm = ({
  onNext,
  isEditMode,
  onEditSubmit,
  handleEditStep,
  step,
  custom,
  formMode,
}: TProductStockProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [expanded, setExpanded] = useState<string[]>([])
  const { formData, setFormData } = useFormContext()
  const initialValues: TInitialValues = {
    stocks: [
      {
        type: formData.stocks?.[0]?.type || '',
        channel: 'magazineluiza',
        branch: formData.stocks?.[0]?.branch || '',
        reserved: formData.stocks?.[0]?.reserved || 0,
        name: formData.stocks?.[0]?.name || '',
        quantity: formData.stocks?.[0]?.quantity || 0,
      },
    ],
    variations: formData.variations,
    fulfillment: Boolean(formData.fulfillment),
  }

  const hasVariations = !!formData.variations?.length
  const bulkModeDefault = isBoolean(formData.bulkMode?.stockForm)
    ? formData.bulkMode?.stockForm
    : true
  const [isBulkMode, setBulkMode] = useState<boolean>(bulkModeDefault)

  const toggleBulkMode = () => {
    setBulkMode(prevBulkMode => !prevBulkMode)
  }

  useEffect(() => {
    if (formData.pendingFieldsVariationFlow) {
      toggleBulkMode()
    }
  }, [formData.pendingFieldsVariationFlow])

  useEffect(() => {
    const submitEdit = async () => {
      if (isSubmitting && isEditMode && onEditSubmit) {
        setIsSubmitting(false)
        setIsLoading(true)
        try {
          await onEditSubmit(formData)
          setIsLoading(false)
        } catch (error) {
          console.error('Error during API call', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    submitEdit()
  }, [isSubmitting, formData, isEditMode, onEditSubmit])

  const onSubmit = (values: TStockInitialValues) => {
    if (
      (values?.fulfillment && isBulkMode) ||
      (values?.variations?.find(variation => variation.fulfillment) && !isBulkMode)
    ) {
      Analytics.ProductStockSaved(formMode, 'com-estoque-magalu')
    } else {
      Analytics.ProductStockSaved(formMode, 'sem-estoque-magalu')
    }

    setFormData((prevData: TProductInitialValues) => ({
      ...prevData,
      stocks: values.stocks,
      variations: values.variations.map(variation => ({
        ...variation,
        stocks: variation.stocks.map(stock => {
          const newStock = isBulkMode ? values.stocks[0] : stock

          return {
            ...newStock,
            channel: 'magazineluiza',
          }
        }),
        fulfillment: isBulkMode ? Boolean(values?.fulfillment) : Boolean(variation?.fulfillment),
      })),
      fulfillment: values.fulfillment,
      bulkMode: {
        ...(prevData.bulkMode || {}),
        stockForm: isBulkMode,
      },
    }))

    setIsSubmitting(true)
    setExpanded([])
    onNext()
  }

  const handleValidation = async ({ validateForm }: Pick<FormikProps<any>, 'validateForm'>) => {
    const errors = await validateForm()
    const { stocks, variations } = errors

    if (stocks && Array.isArray(stocks)) {
      const errorsStocks = stocks[0]

      Object.keys(errorsStocks).forEach(field => {
        if (allowedFields.includes(field)) {
          Analytics.ProductStockValidationError(formMode)
        }
      })
    }

    if (variations && Array.isArray(variations)) {
      variations.forEach(variation => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const errorsStocks = variation?.stocks?.[0]

        if (errorsStocks) {
          Object.keys(errorsStocks).forEach(field => {
            if (allowedFields.includes(field)) {
              Analytics.ProductStockValidationError(formMode)
            }
          })
        }
      })
    }
  }

  const handleVariationsValidation = async (formik: FormikProps<TInitialValues>) => {
    if (hasVariations && !isBulkMode) {
      const errors = await formik.validateForm()
      const { variations } = formik.values

      if (errors.variations && Array.isArray(errors.variations)) {
        const errorPanels = variations
          .map((variation, index) => {
            if (Array.isArray(errors.variations) && errors.variations[index]) {
              return variation.id
            }

            return null
          })
          .filter(Boolean) as string[]

        setExpanded(prev => Array.from(new Set([...prev, ...errorPanels])))
      }
    }
  }

  const handleAccordionChange =
    (panel: string) => (event: React.ChangeEvent<unknown>, isExpanded: boolean) => {
      setExpanded(prev => (isExpanded ? [...prev, panel] : prev.filter(item => item !== panel)))
    }

  const renderVariationHeader = (variationItem: TVariation) => {
    const variationAttributes = variationItem.customAttributes.map(attribute => ({
      label: attribute.value,
      showAvatar: attribute.type === 'color',
      color:
        attribute.type === 'color'
          ? DEFAULT_VARIATION_OPTIONS_BY_TYPE.color.find(
              colorOption => colorOption.title === attribute.value
            )?.color
          : undefined,
    }))

    return (
      <Box className="d-flex align-center mr-3" key={0}>
        <div
          className="d-flex justify-center mr-2"
          style={{
            borderColor: '#CECFD1',
            borderRadius: 4,
            borderWidth: 1,
            borderStyle: 'solid',
            height: 48,
            width: 48,
            overflow: 'hidden',
          }}
        >
          <img
            src={variationItem.images?.[0]?.url}
            alt="Imagem de uma variação do produto"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>

        {variationAttributes.map((attribute, index: number) => (
          <Fragment key={index}>
            <VariationItemView variation={attribute} />
            {index !== variationAttributes.length - 1 && <span className="mr-1">/</span>}
          </Fragment>
        ))}
      </Box>
    )
  }

  const renderStocksFieldsByVariation = ({ values }: FormikProps<TInitialValues>) => {
    return values.variations.map((variationItem: TVariation, index: number) => {
      return (
        <div>
          <Accordion
            key={variationItem.id}
            style={{ boxShadow: 'none' }}
            expanded={expanded.includes(variationItem.id)}
            onChange={handleAccordionChange(variationItem.id)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              {renderVariationHeader(variationItem)}
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} className="py-3 px-2">
                <Grid item md={12}>
                  <StockChannels
                    channels={['magazineluiza']}
                    variation="ALL"
                    variationIndex={index}
                    onChange={() => handleEditStep(step.key)}
                    fulfillmentLink={custom?.fulfillmentLink}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Divider />
        </div>
      )
    })
  }

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={
        hasVariations && !isBulkMode
          ? VariationProductStockFormSchema
          : SimpleProductStockFormSchema
      }
      enableReinitialize
    >
      {formikProps => {
        return (
          <Form className="w-100">
            <CardStep
              description={i18n.t('productNew.stock.description')}
              title={!isEditMode && i18n.t('productNew.stock.title')}
              headerAction={
                hasVariations && (
                  <BulkModeButton isBulkMode={isBulkMode} show onClick={toggleBulkMode} />
                )
              }
            >
              {!isBulkMode ? (
                renderStocksFieldsByVariation(formikProps)
              ) : (
                <StockChannels
                  channels={['magazineluiza']}
                  variation="ALL"
                  onChange={() => handleEditStep(step.key)}
                  fulfillmentLink={custom?.fulfillmentLink}
                />
              )}
              <Stack className="mt-3" direction="row" justifyContent="flex-end" spacing={2}>
                {!isEditMode && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        formikProps.resetForm()
                        handleEditStep(null)
                      }}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        formikProps.handleSubmit()
                        handleVariationsValidation(formikProps)
                        handleValidation({ validateForm: formikProps.validateForm })
                      }}
                    >
                      {i18n.t('productNew.actions.continue')}
                    </Button>
                  </>
                )}

                {isEditMode && (formikProps.dirty || isLoading) && (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => formikProps.resetForm()}
                      className="mr-2"
                    >
                      <span>Cancelar</span>
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        formikProps.handleSubmit()
                        handleVariationsValidation(formikProps)
                        handleValidation({ validateForm: formikProps.validateForm })
                      }}
                      loading={isLoading}
                    >
                      <span>Salvar</span>
                    </Button>
                  </>
                )}
              </Stack>
            </CardStep>
          </Form>
        )
      }}
    </Formik>
  )
}

export default memo(ProductStockForm)
